import { createApi } from '@reduxjs/toolkit/query/react';
import { ReportFilterSet, ReceiptFilterSet } from '../../shared/models/reports.js';
import { baseQuery } from './baseQuery';
import { serialize } from '../../utility/serialize';
import {
  InventoryReportExportRequest,
  KardexPagedResult,
  KardexPreviewRequest,
  InventoryReportScopedRequest,
  PleInventoryPagedResult,
  PleInventoryReportExportRequest,
  PleInventoryReportFilterSet,
  StockValuedPagedResult,
} from '../../shared/models/inventory-report';

const inventoryReportExportResponseHandler = async (response: Response) => {
  if (!response.ok) {
    return response.text();
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery,
  //reporte general
  endpoints: (builder) => ({
    exportGeneralReport: builder.query<Blob, ReportFilterSet>({
      query: (filterSet) => {
        const { selectedColumns, ...otherParams } = filterSet;
        const params = new URLSearchParams();
        Object.entries(otherParams).forEach(([key, value]) => {
          if (value !== undefined) {
            if (Array.isArray(value)) {
              value.forEach((item) => params.append(key, String(item)));
            } else if (value === null) {
              params.append(key, 'null');
            } else if (typeof value === 'object') {
              params.append(key, JSON.stringify(value));
            } else {
              params.append(key, String(value));
            }
          }
        });

        if (selectedColumns) {
          selectedColumns.forEach((column) => {
            params.append('selectedColumns', column);
          });
        }

        return {
          url: `/api/reports/:exportAsXslx?${params.toString()}`,
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    //rporte venta
    exportSalesReport: builder.query<Blob, ReceiptFilterSet>({
      query: (filterSet) => {
        const { selectedColumns, ...otherParams } = filterSet;
        const params = new URLSearchParams();
        Object.entries(otherParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach((item) => params.append(key, String(item)));
            } else if (typeof value === 'object') {
              params.append(key, JSON.stringify(value));
            } else {
              params.append(key, String(value));
            }
          }
        });

        if (selectedColumns) {
          selectedColumns.forEach((column) => {
            params.append('selectedColumns', column);
          });
        }

        return {
          url: `/api/receipts/:exportAsXslx?${params.toString()}`,
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    getKardexPreview: builder.query<KardexPagedResult, KardexPreviewRequest>({
      query: (request) => {
        const { companyId, ...filterSet } = request;
        delete filterSet.companyIds;
        delete filterSet.search;

        return {
          url: `/api/inventoryReports/kardex/${encodeURIComponent(companyId)}/preview?${serialize(
            filterSet
          )}`,
          method: 'GET',
        };
      },
    }),
    getStockValuedPreview: builder.query<StockValuedPagedResult, InventoryReportScopedRequest>({
      query: (request) => {
        const { companyId, ...filterSet } = request;
        delete filterSet.companyIds;
        delete filterSet.search;

        return {
          url: `/api/inventoryReports/stock-valued/${encodeURIComponent(companyId)}/preview?${serialize(
            filterSet
          )}`,
          method: 'GET',
        };
      },
    }),
    getPleInventoryPreview: builder.query<
      PleInventoryPagedResult,
      PleInventoryReportFilterSet & { companyId: string }
    >({
      query: (request) => {
        const { companyId, ...filterSet } = request;
        delete filterSet.companyIds;
        delete filterSet.search;

        return {
          url: `/api/inventoryReports/ple/${encodeURIComponent(companyId)}/preview?${serialize(
            filterSet
          )}`,
          method: 'GET',
        };
      },
    }),
    exportKardexReport: builder.query<string, InventoryReportExportRequest>({
      query: (request) => {
        const { companyId, ...filterSet } = request;
        delete filterSet.companyIds;
        delete filterSet.search;

        return {
          url: `/api/inventoryReports/kardex/${encodeURIComponent(companyId)}/export?${serialize(
            filterSet
          )}`,
          method: 'GET',
          responseHandler: inventoryReportExportResponseHandler,
        };
      },
    }),
    exportStockValuedReport: builder.query<string, InventoryReportExportRequest>({
      query: (request) => {
        const { companyId, ...filterSet } = request;
        delete filterSet.companyIds;
        delete filterSet.search;

        return {
          url: `/api/inventoryReports/stock-valued/${encodeURIComponent(companyId)}/export?${serialize(
            filterSet
          )}`,
          method: 'GET',
          responseHandler: inventoryReportExportResponseHandler,
        };
      },
    }),
    exportPleInventoryReport: builder.query<string, PleInventoryReportExportRequest>({
      query: (request) => {
        const { companyId, ...filterSet } = request;
        delete filterSet.companyIds;
        delete filterSet.search;

        return {
          url: `/api/inventoryReports/ple/${encodeURIComponent(companyId)}/export?${serialize(
            filterSet
          )}`,
          method: 'GET',
          responseHandler: inventoryReportExportResponseHandler,
        };
      },
    }),
  }),
});

export const {
  useLazyExportGeneralReportQuery,
  useLazyExportSalesReportQuery,
  useGetKardexPreviewQuery,
  useGetStockValuedPreviewQuery,
  useGetPleInventoryPreviewQuery,
  useLazyExportKardexReportQuery,
  useLazyExportStockValuedReportQuery,
  useLazyExportPleInventoryReportQuery,
} = reportsApi;
